import { cn } from "@/lib/utils";
import { pathwayOptionLetter } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import { Loader2 } from "lucide-react";
import { ReactNode, isValidElement } from "react";

import Card from "../Card/Card";
import { AnimatedList } from "./AnimatedList";
import { PathwayOptionPicker } from "./PathwayOptionPicker";

export type LevelCardRow = {
  key: string;
  label: ReactNode;
  value: ReactNode;
};

export interface LevelCardProps {
  /** 1-based, as displayed. */
  level: number;
  /** 0-based. */
  selectedOption: number;
  optionCount: number;
  /** What the selected path grants, already formatted for display. */
  rows: LevelCardRow[];
  /** PlayStyles the path grants, as icons on their own row under the stats. */
  icons?: ReactNode[];
  /** Roles the path grants, as chips on their own row under the stats. */
  chips?: ReactNode[];
  onSelectOption?: (option: number) => void;
  readOnly?: boolean;
  /** See PathwayOptionPicker. */
  isOptionAvailable?: (option: number) => boolean;
  onHoverOption?: (option: number | undefined) => void;
  loading?: boolean;
  /** See PathwayOptionPicker. */
  recommendedOption?: number;
  /** Glide, fade and flash the content when the path changes what it grants. */
  animated?: boolean;
  /** The narrower phone card that snap-scrolls. */
  compact?: boolean;
  /**
   * Only the decision: the picker on a level with a choice, "No choice" on
   * one without, and none of the upgrades. For the builder, where the
   * numbers on the card are the generic ones and would mislead.
   */
  choiceOnly?: boolean;
  className?: string;
}

/**
 * One level of an evolution: what it grants and, on a pathway level, which
 * path is chosen and the picker to change it. Presentational — the caller
 * resolves the level's upgrades (see Levels) so this stays free of the
 * catalogue and the label localisation.
 */
export const LevelCard = ({
  level,
  selectedOption,
  optionCount,
  rows,
  icons = [],
  chips = [],
  onSelectOption,
  readOnly = false,
  isOptionAvailable,
  onHoverOption,
  loading = false,
  recommendedOption,
  animated = false,
  compact = false,
  choiceOnly = false,
  className,
}: LevelCardProps) => {
  const { t } = useTranslation("evolutions");
  const hasChoice = optionCount > 1;
  // While a choice resolves the spinner sits in the header, top right, so
  // the pills never change shape under the pointer.
  const spinner = loading && hasChoice && (
    <Loader2
      className="size-3.5 shrink-0 animate-spin text-foreground-muted"
      aria-label={t("pathway.loading")}
    />
  );

  if (choiceOnly) {
    return (
      <Card
        hideBorder
        hoverable={false}
        className={cn(
          "flex flex-col gap-3 p-4",
          compact ? "w-[186px]" : "w-60",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-2 text-sm font-semibold text-foreground">
          <span>{t("pathway.level", { level })}</span>
          {spinner}
        </div>
        <PathwayOptionPicker
          count={optionCount}
          selected={selectedOption}
          onSelect={onSelectOption}
          readOnly={readOnly}
          isOptionAvailable={isOptionAvailable}
          onHover={onHoverOption}
          loading={loading}
          recommended={recommendedOption}
        />
      </Card>
    );
  }

  return (
    <Card
      hideBorder
      hoverable={false}
      className={cn(
        // No explicit height: both the grid and the scroll row stretch the
        // card to the tallest sibling on their own.
        "flex flex-col gap-3 p-4",
        compact ? "w-[186px]" : "w-60",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <span>{t("pathway.level", { level })}</span>
          {hasChoice && (
            <>
              <span
                className="size-1 rounded-full bg-evolution-secondary"
                aria-hidden
              />
              <div className="text-sm text-foreground-muted">
                {t("pathway.path", {
                  path: pathwayOptionLetter(selectedOption),
                })}
              </div>
            </>
          )}
        </div>
        {spinner}
      </div>
      {hasChoice && !readOnly && (
        <div className="border-b border-edge-strong pb-3">
          <PathwayOptionPicker
            count={optionCount}
            selected={selectedOption}
            onSelect={onSelectOption}
            isOptionAvailable={isOptionAvailable}
            onHover={onHoverOption}
            loading={loading}
            recommended={recommendedOption}
          />
        </div>
      )}

      <LevelCardBody
        animated={animated}
        transition={{
          version: [
            ...rows.map((row) => `${row.key}=${textOf(row.value)}`),
            ...chips.map(rowKeyOf),
            ...icons.map(rowKeyOf),
          ].join("|"),
          resetKey: level,
        }}
      >
        {rows.map((row) => (
          <div
            key={row.key}
            data-row-key={row.key}
            className="-mx-2 flex items-center justify-between gap-3 rounded-lg px-2"
          >
            <span className="min-w-0 truncate text-foreground-muted">
              {row.label}
            </span>
            <span className="shrink-0 font-semibold text-upgrade">
              {row.value}
            </span>
          </div>
        ))}
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {chips}
          </div>
        )}
        {icons.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {icons}
          </div>
        )}
        {rows.length === 0 && icons.length === 0 && chips.length === 0 && (
          <div className="text-xs text-foreground-muted">
            {t("detail.noUpgrades")}
          </div>
        )}
      </LevelCardBody>
    </Card>
  );
};

/** The version string only needs to change when what is drawn changes. */
const textOf = (node: ReactNode): string =>
  typeof node === "string" || typeof node === "number" ? String(node) : "";

/** The `data-row-key` a keyed icon or chip wrapper from Levels carries. */
const rowKeyOf = (node: ReactNode): string =>
  isValidElement<{ "data-row-key"?: string }>(node)
    ? (node.props["data-row-key"] ?? String(node.key))
    : "";

const LevelCardBody = ({
  animated,
  transition,
  children,
}: {
  animated: boolean;
  transition: { version: string; resetKey: number };
  children: ReactNode;
}) => {
  const className = "flex flex-1 flex-col gap-1.5 text-sm";
  return animated ? (
    <AnimatedList transition={transition} className={className}>
      {children}
    </AnimatedList>
  ) : (
    <div className={className}>{children}</div>
  );
};
